import type { PlayerListItem } from "../shared/types/contracts";

export const JERSEY_CAPTIONS = [
  "WEARING THE JERSEY OF SHAME",
  "CERTIFIED WHIFF ARTIST",
  "STRIKEOUT ROYALTY",
  "THE JERSEY CHOSE THEM",
  "PROFESSIONAL WINDMILL",
  "BREEZE GENERATOR",
  "CHIEF OF THE WHIFF TRIBE",
  "WHIFF HAPPENS",
] as const;

export interface BattingPlayer {
  id: string | null;
  name: string;
  num: number | null;
}

export interface BatterSelection {
  batter: BattingPlayer | null;
  nextBatters: BattingPlayer[];
}

export const FORFEIT_SCORE = 8;

export interface ScoreSummary {
  ourScore: number;
  oppScore: number;
  result: "W" | "L" | "T";
  finalLabel: string;
  forfeit: boolean;
}

export function buildInviteUrl(
  origin: string,
  teamId: string,
  token: string | null,
): string | null {
  return token ? `${origin}/join/${teamId}#token=${token}` : null;
}

export function selectBattingList(
  gameLineup: BattingPlayer[] | null,
  lineupSlots: Array<string | null>,
  players: PlayerListItem[],
): BattingPlayer[] {
  if (gameLineup) return gameLineup;
  return lineupSlots
    .filter((id): id is string => Boolean(id))
    .map((id) => players.find((player) => player.id === id))
    .filter((player): player is PlayerListItem => Boolean(player));
}

export function selectBatters(
  battingList: BattingPlayer[],
  batterIndex: number,
): BatterSelection {
  if (battingList.length === 0) return { batter: null, nextBatters: [] };
  return {
    batter: battingList[batterIndex % battingList.length] ?? null,
    nextBatters: Array.from(
      { length: 3 },
      (_, index) =>
        battingList[(batterIndex + index + 1) % battingList.length] ?? null,
    ).filter((player): player is BattingPlayer => Boolean(player)),
  };
}

export function summarizeScore(
  ourRunsByInning: number[],
  opponentRunsByInning: number[],
  forfeitWinner?: "us" | "them" | null,
): ScoreSummary {
  if (forfeitWinner) {
    const won = forfeitWinner === "us";
    const ourScore = won ? FORFEIT_SCORE : 0;
    const oppScore = won ? 0 : FORFEIT_SCORE;
    const result = won ? "W" : "L";
    return {
      ourScore,
      oppScore,
      result,
      finalLabel: `FINAL — ${result} ${ourScore}–${oppScore} · FORFEIT`,
      forfeit: true,
    };
  }
  const ourScore = ourRunsByInning.reduce((total, runs) => total + runs, 0);
  const oppScore = opponentRunsByInning.reduce(
    (total, runs) => total + runs,
    0,
  );
  const result = ourScore > oppScore ? "W" : ourScore < oppScore ? "L" : "T";
  return {
    ourScore,
    oppScore,
    result,
    finalLabel: `FINAL — ${result} ${ourScore}–${oppScore}`,
    forfeit: false,
  };
}

export function buildInningLabel(
  batting: boolean,
  isHome: boolean,
  inning: number,
): string {
  return `${batting !== isHome ? "▲" : "▼"} INN ${inning}`;
}

export function isOpenInning(inning: number, openInning: boolean): boolean {
  return inning >= 9 || openInning;
}

export function selectJerseyCaption(randomValue: number): string {
  return (
    JERSEY_CAPTIONS[Math.floor(randomValue * JERSEY_CAPTIONS.length)] ??
    JERSEY_CAPTIONS[0]
  );
}

export function createJerseyMoment(
  batterId: string | null | undefined,
  randomValue: number,
): { batterId: string | null | undefined; caption: string } {
  return {
    batterId,
    caption: selectJerseyCaption(randomValue),
  };
}
